import React, { useMemo, useContext } from 'react';
import {
    useBreakpoint,
    Spacing,
    ResponsiveContext,
    ResponsiveState,
} from '@landr/maestro';
import { getResponsiveValue } from 'helpers';
import Wrapper from 'components/CommonUi/Wrapper';
import GenericCarouselContext from './GenericCarouselContext';
import { Container, GenericCarouselStyled } from './GenericCarousel.styled';
import { getCardItems } from './GenericCarousel.helpers';
import { GenericCarouselProps } from './GenericCarousel.type';
import { Header } from './components';

const DefaultImageHeight = 192;

function _GenericCarousel<P>({
    id = '',
    title,
    items = [],
    cardComponent: CardComponent,
    fallbackComponent: FallbackComponent,
    isFetching,
    imageHeight = DefaultImageHeight,
    carouselUrl,
    showMoreCard,
    linkLocation = 'Title',
    carouselGroupIndex,
    slidesPerPage,
    showHeader = true,
    observerComponent,
    noMoreItems = false,
    containerRef,
    ...props
}: GenericCarouselProps<P>) {
    const { breakpoints } = useContext<ResponsiveState>(ResponsiveContext);
    const isMobile = useBreakpoint('md');

    // MEMO
    const responsiveGutter: Spacing = useMemo(
        () =>
            getResponsiveValue<Spacing>(
                {
                    md: 'sm',
                    xxl: 'md',
                    default: 'xl',
                },
                breakpoints,
            ),
        [breakpoints],
    );

    // SKELETON
    const skeletonData = useMemo((): Array<React.ReactElement> => {
        const dataPlaceholder: Array<React.ReactElement> = [];
        let numberOfSlidesPerPage;

        if (typeof slidesPerPage === 'number') {
            numberOfSlidesPerPage = slidesPerPage;
        } else {
            numberOfSlidesPerPage = getResponsiveValue<number>(slidesPerPage, {
                xxl: true,
            });
        }

        for (let i = 0; i < numberOfSlidesPerPage; i++) {
            dataPlaceholder.push(<FallbackComponent key={`fallback ${i}`} />);
        }

        return dataPlaceholder;
    }, [slidesPerPage]);

    // ITEMS
    const cardItems = getCardItems(
        items,
        CardComponent,
        isFetching,
        noMoreItems,
        skeletonData,
        FallbackComponent,
        observerComponent,
        showMoreCard,
    );

    return (
        <Container
            ref={containerRef}
            data-carousel-group-index={
                typeof carouselGroupIndex === 'number'
                    ? carouselGroupIndex
                    : null
            }
        >
            <GenericCarouselContext.Provider
                value={{
                    carouselId: id,
                    carouselName: title,
                    carouselUrl: carouselUrl,
                    linkLocation: linkLocation,
                }}
            >
                {showHeader && <Header />}

                <Wrapper isNegative>
                    <GenericCarouselStyled
                        items={cardItems}
                        gutter={responsiveGutter}
                        arrowOffset={30}
                        slidesPerPage={slidesPerPage}
                        itemsOffset={isMobile ? 'xl' : undefined}
                        {...props}
                    />
                </Wrapper>
            </GenericCarouselContext.Provider>
        </Container>
    );
}

export const GenericCarousel = React.memo(_GenericCarousel);

export default GenericCarousel;
